import React, { useEffect, useMemo, useState } from "react";
import { TahsilatMatchNote, BulkTahsilatFillButton } from "@/components/prim/TahsilatMatchNote";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { Clock, Check, X, CheckCircle2, FileText, AlertTriangle, AlertCircle, GitMerge, MessageSquare, BarChart3, Settings, Edit, Plus, Trash2, Sparkles, RefreshCw, Eye, Bell, MailOpen, Lock, ShieldAlert, RotateCcw, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatNum, fmtDate } from "@/lib/api";
import PrimModal from "@/components/prim/PrimModal";
import VarlikAuditModal from "@/components/prim/VarlikAuditModal";
import GidenScopeAuditModal from "@/components/prim/GidenScopeAuditModal";
import { useAuth } from "@/lib/auth";
import { DateInput } from "@/components/DateInput";
import { MonthSummary } from "@/components/prim/MonthSummary";

const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const primPer = (r) => r.prim_ayi || (r.tarih || "").slice(0, 7);
const perLabel = (p) => `${p.slice(0, 4)} ${AY_ADLARI[Number(p.slice(5, 7)) - 1] || ""}`;

const STALE_DAYS = 7;

function daysSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}

// 8 soft pastel bg classes to visually distinguish conflict groups
const CONFLICT_COLORS = [
  "bg-amber-50 hover:bg-amber-100",
  "bg-rose-50 hover:bg-rose-100",
  "bg-fuchsia-50 hover:bg-fuchsia-100",
  "bg-violet-50 hover:bg-violet-100",
  "bg-sky-50 hover:bg-sky-100",
  "bg-teal-50 hover:bg-teal-100",
  "bg-lime-50 hover:bg-lime-100",
  "bg-orange-50 hover:bg-orange-100",
];

export default function PrimPendingTab() {
  const { isAdmin, isMuhasebeci, user } = useAuth();
  const canSeePrimRate = !!user?.permissions?.canSeePrimRate;
  const [rows, setRows] = useState([]);
  const [matches, setMatches] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [conflictGroupCount, setConflictGroupCount] = useState(0);
  const [merging, setMerging] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null); // {mode:'single'|'bulk', row?, ids}
  const [templates, setTemplates] = useState([]);
  const [hitRate, setHitRate] = useState(null);

  const [showApprovedToggle, setShowApprovedToggle] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState("pending"); // "pending" | "approved" | "all"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [noInfazOnly, setNoInfazOnly] = useState(false);
  const [oldOnly, setOldOnly] = useState(false);
  const [primMonth, setPrimMonth] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, h] = await Promise.all([
        api.get(`/prim/pending${showApprovedToggle ? "?include_approved=true" : ""}`),
        api.get("/prim/conflict-stats").catch(() => ({ data: null })),
        api.get("/prim/ai-hit-rate?days=30").catch(() => ({ data: null })),
      ]);
      // Sort: muhasebeci-approved first (so admin sees them at the top), then by created_at desc
      const sortedRows = [...(r.data?.rows || [])].sort((a, b) => {
        const ap = a.muhasebeci_onay ? 0 : 1;
        const bp = b.muhasebeci_onay ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
      setRows(sortedRows);
      api.post("/prim/tahsilat-match", { ids: sortedRows.map((x) => x.id) })
        .then((m) => setMatches(m.data?.matches || {})).catch(() => setMatches({}));
      setConflictGroupCount(r.data?.conflict_group_count || 0);
      setStats(s.data || null);
      setHitRate(h.data || null);
      setSelected(new Set());
    } catch (e) {
      toast.error("Onay bekleyen primler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [showApprovedToggle]);

  // Preload reject templates once
  useEffect(() => {
    api.get("/prim/reject-templates")
      .then((r) => setTemplates(r.data?.rows || []))
      .catch(() => setTemplates([]));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const [blockDialog, setBlockDialog] = useState(null); // {mode:'single'|'bulk', ids, blocking_prims, blocked_prims?}
  const [quickView, setQuickView] = useState(null); // prim id to quick-view
  const [notePopover, setNotePopover] = useState(null); // prim id whose note editor is open
  const [editing, setEditing] = useState(null); // prim row being edited via PrimModal
  const [personnel, setPersonnel] = useState([]);
  const [notifs, setNotifs] = useState([]); // admin notifications (muhasebeci notes)
  const [notifsCollapsed, setNotifsCollapsed] = useState(false);
  const [auditJump, setAuditJump] = useState(null); // "varlik" | "giden" | null

  const reloadNotifs = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/admin-notifications?unread_only=false&limit=50");
      setNotifs(r.data?.rows || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    if (isAdmin) reloadNotifs();
  }, [isAdmin]);

  const markNotifRead = async (id) => {
    try {
      await api.post(`/admin-notifications/${id}/mark-read`);
      reloadNotifs();
    } catch (e) {
      toast.error("İşaretleme başarısız");
    }
  };

  const markAllNotifsRead = async () => {
    try {
      await api.post("/admin-notifications/mark-all-read");
      toast.success("Tüm bildirimler okundu");
      reloadNotifs();
    } catch (e) {
      toast.error("Hepsini okundu yapılamadı");
    }
  };

  useEffect(() => {
    api.get("/personnel")
      .then((r) => setPersonnel(Array.isArray(r.data) ? r.data : (r.data?.rows || [])))
      .catch(() => setPersonnel([]));
  }, []);

  const unapproveOne = async (row) => {
    if (!window.confirm(`"${row.alacakli_adi || row.dosya_adi || row.id}" onayı kaldırılacak ve tekrar pending durumuna dönecek. Devam?`)) return;
    try {
      await api.post(`/prim/${row.id}/unapprove`);
      toast.success("Prim onayı kaldırıldı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onay kaldırılamadı");
    }
  };


  const noInfazConfirm = (ids, verb) => {
    const list = rows.filter((r) => ids.includes(r.id) && matches[r.id] && !matches[r.id].infaz);
    if (!list.length) return true;
    const names = list.slice(0, 8).map((r) => `• ${r.dosya_adi || r.icra_foy || "—"}`).join("\n");
    return window.confirm(`Dikkat: ${list.length} primin dosyasında İNFAZ kaydı yok.\n${names}${list.length > 8 ? `\n… +${list.length - 8}` : ""}\n\nYine de ${verb}?`);
  };

  const approveOne = async (id) => {
    if (!noInfazConfirm([id], "onaylansın mı")) return;
    try {
      await api.post(`/prim/${id}/approve`);
      toast.success("Prim onaylandı");
      load();
    } catch (e) {
      const data = e?.response?.data || {};
      if (e?.response?.status === 409 && data.blocked) {
        // Open block dialog with blocking prims
        setBlockDialog({
          mode: "single",
          ids: [id],
          blocking_prims: data.blocking_prims || [],
          message: data.message || "",
        });
        return;
      }
      toast.error(data.detail || "Onay başarısız");
    }
  };

  const confirmOverrideApprove = async () => {
    if (!blockDialog) return;
    try {
      if (blockDialog.mode === "single") {
        await api.post(`/prim/${blockDialog.ids[0]}/approve?override=true`);
        toast.success("Prim ADMIN OVERRIDE ile onaylandı");
      } else {
        const r = await api.post("/prim/approve-bulk", { ids: blockDialog.ids, override: true });
        toast.success(`${r.data.approved} prim ADMIN OVERRIDE ile onaylandı`);
      }
      setBlockDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override onay başarısız");
    }
  };

  const confirmMergeInto = async (pendingId, targetId) => {
    try {
      const r = await api.post("/prim/merge-into-approved", {
        pending_id: pendingId,
        target_approved_id: targetId,
      });
      toast.success(
        `Birleştirildi · Tahsilat ${(r.data?.tahsilat_miktari || 0).toLocaleString("tr-TR")} ₺ · ${r.data?.giden_kisiler?.length || 0} giden kişi`
      );
      setBlockDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Birleştirme başarısız");
    }
  };

  const toggleMuhasebeciOnay = async (row) => {
    const has = !!row.muhasebeci_onay;
    try {
      if (has) {
        await api.post(`/prim/${row.id}/muhasebeci-unapprove`);
        toast.success("Ön onay geri alındı");
      } else {
        if (!noInfazConfirm([row.id], "ön onaylansın mı")) return;
        await api.post(`/prim/${row.id}/muhasebeci-approve`);
        toast.success("Ön onaylandı · admin'in final onayı bekleniyor");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ön onay işlemi başarısız");
    }
  };

  const bulkMuhasebeciOnay = async () => {
    if (selected.size === 0) { toast.error("Önce prim seçin"); return; }
    // Determine mode: if ALL selected are already pre-approved → unapprove, else approve
    const selectedRows = rows.filter((r) => selected.has(r.id));
    const allPre = selectedRows.every((r) => r.muhasebeci_onay);
    const unapprove = allPre;
    const verb = unapprove ? "ön onayı geri al" : "toplu ön onayla";
    if (!window.confirm(`${selected.size} prim ${verb}insin mi?`)) return;
    if (!unapprove && !noInfazConfirm(Array.from(selected), "ön onaylansın mı")) return;
    try {
      const r = await api.post("/prim/muhasebeci-approve-bulk", {
        ids: Array.from(selected),
        unapprove,
      });
      toast.success(unapprove
        ? `${r.data.updated} prim ön onayı geri alındı`
        : `${r.data.updated} prim ön onaylandı · admin'in final onayı bekleniyor`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu ön onay başarısız");
    }
  };

  const rejectOne = (row) => {
    setRejectDialog({ mode: "single", row, ids: [row.id] });
  };

  const bulkReject = () => {
    if (selected.size === 0) { toast.error("Önce prim seçin"); return; }
    setRejectDialog({ mode: "bulk", ids: Array.from(selected) });
  };

  const reloadTemplates = async () => {
    try {
      const r = await api.get("/prim/reject-templates");
      setTemplates(r.data?.rows || []);
    } catch { /* ignore */ }
  };

  const submitReject = async (reason, templateIds, suggestionId, personelNotes) => {
    if (!rejectDialog) return;
    const { mode, ids, row } = rejectDialog;
    const payload = {
      reason,
      template_ids: templateIds || [],
      suggestion_id: suggestionId || null,
    };
    if (personelNotes && Object.keys(personelNotes).length > 0) {
      payload.personel_notes = personelNotes;
    }
    try {
      if (mode === "single") {
        const r = await api.post(`/prim/${row.id}/reject`, payload);
        const n = r.data?.notified_personel || 0;
        toast.success(n > 0 ? `Reddedildi · ${n} personel'e bildirim gitti` : "Reddedildi · silindi");
      } else {
        const r = await api.post("/prim/reject-bulk", { ids, ...payload });
        const n = r.data?.notified_personel || 0;
        const pc = r.data?.personalized_notes || 0;
        const suffix = pc > 0 ? ` · ${pc} kişisel not` : "";
        toast.success(n > 0
          ? `${r.data.rejected} prim reddedildi · ${n} bildirim gitti${suffix}`
          : `${r.data.rejected} prim reddedildi · silindi`);
      }
      setRejectDialog(null);
      load();
      // Refresh templates so chip usage counts update immediately
      reloadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddet başarısız");
    }
  };

  const bulkApprove = async (idsArg) => {
    const ids = Array.isArray(idsArg) ? idsArg : Array.from(selected);
    if (ids.length === 0) { toast.error("Önce prim seçin"); return; }
    if (!Array.isArray(idsArg) && !window.confirm(`${ids.length} prim toplu olarak onaylansın mı?`)) return;
    if (!noInfazConfirm(ids, "onaylansın mı")) return;
    try {
      const r = await api.post("/prim/approve-bulk", { ids });
      toast.success(`${r.data.approved} prim onaylandı`);
      load();
    } catch (e) {
      const data = e?.response?.data || {};
      if (e?.response?.status === 409 && data.blocked) {
        setBlockDialog({
          mode: "bulk",
          ids,
          blocked_prims: data.blocked_prims || [],
          message: data.message || "",
        });
        return;
      }
      toast.error(data.detail || "Toplu onay başarısız");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) { toast.error("Önce prim seçin"); return; }
    const ids = Array.from(selected);
    const first = window.confirm(
      `⚠️ ${ids.length} onay bekleyen prim KALICI OLARAK SİLİNECEK.\n\n• Prim onay geçmişi silinir\n• Daha sonra onaylanamaz / reddedilemez\n\nDevam?`
    );
    if (!first) return;
    const confirm2 = window.prompt(`Güvenlik için SİL yazarak onaylayın (${ids.length} kayıt):`);
    // Normalize Turkish variants (sil / Sil / SIL / SİL) → compare to "SIL"
    const normalized = (confirm2 || "").trim().replace(/i/g, "I").replace(/İ/g, "I").toUpperCase();
    if (normalized !== "SIL") {
      toast.info("İşlem iptal edildi");
      return;
    }
    try {
      const r = await api.post("/prim/bulk-delete", { ids });
      toast.success(`${r.data?.deleted || 0} prim silindi${r.data?.not_found ? ` · ${r.data.not_found} bulunamadı` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toplu silme başarısız");
    }
  };

  const mergeSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length < 2) {
      toast.error("Birleştirmek için en az 2 prim seçin");
      return;
    }
    // All selected must share the same conflict_group_id
    const picked = rows.filter((r) => selected.has(r.id));
    const groupIds = new Set(picked.map((r) => r.conflict_group_id).filter(Boolean));
    if (groupIds.size !== 1) {
      toast.error("Birleştirme için aynı föy+dosya grubundan primler seçin (aynı renkli satırlar)");
      return;
    }
    if (!window.confirm(`${ids.length} prim tek primde birleştirilsin mi?\n\n• İlk prim korunur\n• Gidenler birleştirilir (max 4)\n• Diğer primler silinir`)) return;
    setMerging(true);
    try {
      const r = await api.post("/prim/pending/merge", { ids });
      toast.success(`${ids.length} prim birleştirildi → tek prim (${r.data.giden_kisiler?.length || 0} giden)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Birleştirme başarısız");
    } finally {
      setMerging(false);
    }
  };

  // Sort: conflict groups first (grouped together), then singles by date
  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const ag = a.conflict_group_id || 999999;
      const bg = b.conflict_group_id || 999999;
      if (ag !== bg) return ag - bg;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return copy;
  }, [rows]);

  // Filtered subset shown in the table (chip filter + date range).
  const displayRows = useMemo(() => {
    return sortedRows.filter((r) => {
      if (approvalFilter === "approved" && r.onaylandi !== true) return false;
      if (approvalFilter === "pending" && r.onaylandi === true) return false;
      if (dateFrom && (r.tarih || "") < dateFrom) return false;
      if (dateTo && (r.tarih || "") > dateTo) return false;
      if (noInfazOnly && (!matches[r.id] || matches[r.id].infaz)) return false;
      if (oldOnly && !(matches[r.id]?.eski_tahsilat && !matches[r.id]?.eski_check)) return false;
      if (primMonth && primPer(r) !== primMonth) return false;
      return true;
    });
  }, [sortedRows, approvalFilter, dateFrom, dateTo, noInfazOnly, oldOnly, matches, primMonth]);
  const monthOpts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { const p = primPer(r); if (p) c[p] = (c[p] || 0) + 1; });
    return Object.keys(c).sort().reverse().map((p) => ({ p, n: c[p] }));
  }, [rows]);
  const noInfazCount = rows.filter((r) => matches[r.id] && !matches[r.id].infaz).length;
  const oldCount = rows.filter((r) => matches[r.id]?.eski_tahsilat && !matches[r.id]?.eski_check).length;
  const colSort = useColumnSort(displayRows, [], {
    giden: (r) => (r.giden_isimleri || []).filter(Boolean).join(", "),
    giren: (r) => r.created_by_email,
    not: (r) => (r.muhasebeci_note ? 1 : 0),
  });

  const canMergeSelected = (() => {
    if (selected.size < 2) return false;
    const picked = rows.filter((r) => selected.has(r.id));
    const groupIds = new Set(picked.map((r) => r.conflict_group_id).filter(Boolean));
    return groupIds.size === 1;
  })();

  const totalMoneyPending = rows.reduce((a, r) => a + (r.odenecek_toplam || 0), 0);
  const staleRows = rows.filter((r) => daysSince(r.created_at) >= STALE_DAYS);

  // Status counters used by the chip filter bar.
  const approvedTotal = rows.filter((r) => r.onaylandi === true).length;
  const pendingTotal = rows.length - approvedTotal;

  return (
    <div className="space-y-4" data-testid="prim-pending-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500 text-white grid place-items-center">
            <Clock size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-extrabold text-slate-900 leading-tight">
              Onay Bekleyen Primler
            </div>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
              {rows.length} kayıt{canSeePrimRate ? ` · Toplam Ödenecek: ${formatNum(totalMoneyPending)} ₺` : ""}
              {selected.size > 0 && <span className="ml-2 text-indigo-600">· Seçili: {selected.size}</span>}
            </div>
          </div>
        </div>
        {selected.size > 0 && isAdmin && (
          <div className="flex items-center gap-2">
            {canMergeSelected && (
              <button
                data-testid="pending-merge-selected"
                onClick={mergeSelected}
                disabled={merging}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                title="Aynı föy+dosya ile açılan primleri tek primde birleştir"
              >
                <GitMerge size={16} /> {merging ? "Birleşiyor..." : `Birleştir (${selected.size})`}
              </button>
            )}
            <BulkTahsilatFillButton ids={[...selected]} onDone={load} />
            <button
              data-testid="pending-bulk-preapprove"
              onClick={bulkMuhasebeciOnay}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
              title="Seçili primleri muhasebeci ön onayına al / geri al"
            >
              <CheckCircle2 size={16} /> Ön Onay ({selected.size})
            </button>
            <button
              data-testid="pending-bulk-reject"
              onClick={bulkReject}
              className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              <X size={16} /> Seçilenleri Reddet ({selected.size})
            </button>
            <button
              data-testid="pending-bulk-approve"
              onClick={bulkApprove}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              <CheckCircle2 size={16} /> Seçilenleri Onayla ({selected.size})
            </button>
            <button
              data-testid="pending-bulk-delete"
              onClick={bulkDelete}
              className="flex items-center gap-2 bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
              title="Seçili onay bekleyen primleri kalıcı olarak sil"
            >
              <Trash2 size={16} /> Sil ({selected.size})
            </button>
          </div>
        )}
        {selected.size > 0 && !isAdmin && (
          <div className="flex items-center gap-2">
            <BulkTahsilatFillButton ids={[...selected]} onDone={load} />
            <button
              data-testid="pending-bulk-preapprove"
              onClick={bulkMuhasebeciOnay}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
              title="Seçili primleri toplu ön onayla / geri al"
            >
              <CheckCircle2 size={16} /> Toplu Ön Onay ({selected.size})
            </button>
            <button
              data-testid="pending-bulk-reject"
              onClick={bulkReject}
              className="flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
              title="Seçili primleri toplu reddet · personele bildirim gider"
            >
              <X size={16} /> Toplu Reddet ({selected.size})
            </button>
            {isMuhasebeci && (
              <button
                data-testid="pending-bulk-delete"
                onClick={bulkDelete}
                className="flex items-center gap-2 bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
                title="Seçili onay bekleyen primleri kalıcı olarak sil"
              >
                <Trash2 size={16} /> Sil ({selected.size})
              </button>
            )}
          </div>
        )}
        {!isAdmin && (
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="pending-readonly-banner">
            Düzenle · Ön Onay · Reddet serbest · Final onay admin yetkisindedir
          </div>
        )}
      </div>

      {/* Filtre satırı — onay durumu + tarih aralığı */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="pending-filter-bar">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600 shrink-0">Onay durumu:</span>
        <button
          type="button"
          data-testid="pending-filter-pending"
          onClick={() => setApprovalFilter("pending")}
          className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
            approvalFilter === "pending"
              ? "bg-amber-500 text-white border-amber-500"
              : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
          }`}
        >
          Onay Bekleyen
          <span className={`text-[9px] font-bold ${approvalFilter === "pending" ? "text-amber-100" : "text-slate-400"}`}>{pendingTotal}</span>
        </button>
        <button
          type="button"
          data-testid="pending-filter-approved"
          onClick={() => {
            setApprovalFilter("approved");
            if (!showApprovedToggle) setShowApprovedToggle(true);
          }}
          className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
            approvalFilter === "approved"
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          }`}
        >
          Onaylanan
          <span className={`text-[9px] font-bold ${approvalFilter === "approved" ? "text-emerald-100" : "text-slate-400"}`}>{approvedTotal}</span>
        </button>
        <button
          type="button"
          data-testid="pending-filter-all"
          onClick={() => {
            setApprovalFilter("all");
            if (!showApprovedToggle) setShowApprovedToggle(true);
          }}
          className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${
            approvalFilter === "all"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          }`}
        >
          Hepsi · {rows.length}
        </button>
        <span className="w-px h-5 bg-slate-200 mx-1"></span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Tarih:</span>
        <DateInput
          data-testid="pending-filter-date-from"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="text-xs px-2 py-1 border border-slate-300 rounded"
          title="Başlangıç"
        />
        <span className="text-xs text-slate-400">→</span>
        <DateInput
          data-testid="pending-filter-date-to"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="text-xs px-2 py-1 border border-slate-300 rounded"
          title="Bitiş"
        />
        {(dateFrom || dateTo || primMonth || noInfazOnly || oldOnly) && (
          <button
            type="button"
            onClick={() => { setDateFrom(""); setDateTo(""); setPrimMonth(""); setNoInfazOnly(false); setOldOnly(false); }}
            data-testid="pending-filter-date-clear"
            className="text-[11px] text-slate-500 hover:text-slate-800 underline"
          >
            Temizle
          </button>
        )}
        <span className="w-px h-5 bg-slate-200 mx-1"></span>
        <select value={primMonth} onChange={(e) => setPrimMonth(e.target.value)} data-testid="pending-filter-prim-month"
          title="Primin ait olduğu aya göre filtrele (Prim Ayı değiştirildiyse o ay)"
          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border outline-none ${primMonth ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"}`}>
          <option value="">Prim Ayı: Tümü</option>
          {monthOpts.map((o) => <option key={o.p} value={o.p}>{perLabel(o.p)} · {o.n}</option>)}
        </select>
        {primMonth && (
          <MonthSummary rows={displayRows} label={perLabel(primMonth)} canSeePrim={canSeePrimRate}
            canApprove={isAdmin} onApprove={(ids, msg) => { if (window.confirm(msg)) bulkApprove(ids); }} />
        )}
        {(isAdmin || isMuhasebeci) && (
          <>
            <span className="w-px h-5 bg-slate-200 mx-1"></span>
            <button
              type="button"
              data-testid="pending-filter-no-infaz"
              onClick={() => setNoInfazOnly((v) => !v)}
              title="Sadece dosyasında İNFAZ kaydı olmayan primleri göster"
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${
                noInfazOnly ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"
              }`}
            >
              İnfazsız · {noInfazCount}
            </button>
            <button
              type="button"
              data-testid="pending-filter-old-tahsilat"
              onClick={() => setOldOnly((v) => !v)}
              title="Prim tarihi ile son tahsilat tarihi arasında 3 aydan uzun ara olan primleri göster"
              className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${
                oldOnly ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
              }`}
            >
              Eski tahsilat · {oldCount}
            </button>
          </>
        )}
        <span className="ml-auto text-[11px] font-semibold text-slate-600" data-testid="pending-filter-count">
          Gösterilen: {displayRows.length} / {rows.length}
        </span>
      </div>

      {/* AI Isabet Oranı (son 30 gün) */}
      {hitRate && hitRate.total_suggestions > 0 && (
        <div
          data-testid="ai-hit-rate-card"
          className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-600 text-white grid place-items-center">
              <Sparkles size={16} />
            </div>
            <div>
              <div className="text-xs font-bold text-violet-900 uppercase tracking-wider">
                AI Şablon Önerisi İsabet Oranı · Son {hitRate.days} gün
              </div>
              <div className="text-[11px] text-violet-800">
                <strong className="text-violet-900 text-sm">%{hitRate.hit_rate}</strong>
                <span className="ml-1">({hitRate.applied_ai}/{hitRate.with_submit} oturum)</span>
                <span className="opacity-75 ml-2">
                  · {hitRate.total_suggestions} öneri verildi · {hitRate.applied_any} reddet uygulandı
                </span>
              </div>
            </div>
          </div>
          {hitRate.top_hits && hitRate.top_hits.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="ai-hit-rate-top">
              <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                En çok uygulanan:
              </span>
              {hitRate.top_hits.map((h) => (
                <span
                  key={h.template_id}
                  data-testid={`ai-hit-${h.template_id}`}
                  className="inline-flex items-center gap-1 bg-white border border-violet-300 rounded-full px-2 py-0.5 text-[10px] font-semibold text-violet-900"
                  title={`[${h.category}] · ${h.hits} kez AI'den gelip uygulandı`}
                >
                  {h.label}
                  <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold font-mono-tab bg-violet-600 text-white">
                    {h.hits}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Historical duplicate (prior months) banner */}
      {(() => {
        const historicalFlagged = rows.filter((r) => {
          const h = r.historical_dup || {};
          return (h.foy_count || 0) + (h.esas_count || 0) > 0;
        });
        if (historicalFlagged.length === 0) return null;
        return (
          <div
            data-testid="pending-hist-banner"
            className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex flex-wrap items-center justify-between gap-2"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} />
              <span>
                <strong>{historicalFlagged.length}</strong> prim'de önceki aylarda aynı Föy veya Esas kullanılmış — onaylamadan önce
                satırdaki <strong className="text-amber-900 bg-amber-200 rounded px-1">Önceden N×</strong> rozetine bakın.
              </span>
            </div>
            <button
              data-testid="pending-select-hist"
              onClick={() => setSelected(new Set(historicalFlagged.map((r) => r.id)))}
              className="inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 rounded-md px-2 py-0.5 font-semibold"
            >
              Bu {historicalFlagged.length} Kaydı Seç
            </button>
          </div>
        );
      })()}


      {/* Admin notification sticky — muhasebeci notları */}
      {isAdmin && notifs.filter((n) => !n.read_at && n.entity_type !== "tahsilat_missing_prim" && n.entity_type !== "tahsilat_gonderme_bugun" && n.entity_type !== "personnel_added" && n.entity_type !== "advance_request").length > 0 && (
        <div
          data-testid="admin-notif-sticky"
          className="sticky top-0 z-20 bg-amber-50 border-2 border-amber-400 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between gap-2 p-3 border-b border-amber-200 bg-amber-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500 text-white grid place-items-center relative">
                <Bell size={14} />
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-white text-amber-700 rounded-full border border-amber-300 inline-flex items-center justify-center">
                  {notifs.filter((n) => !n.read_at && n.entity_type !== "tahsilat_missing_prim" && n.entity_type !== "tahsilat_gonderme_bugun" && n.entity_type !== "personnel_added" && n.entity_type !== "advance_request").length}
                </span>
              </div>
              <div>
                <div className="font-display font-extrabold text-amber-900 text-sm">
                  Muhasebeci Notları
                </div>
                <div className="text-[11px] text-amber-800 font-semibold uppercase tracking-wider">
                  {notifs.filter((n) => !n.read_at && n.entity_type !== "tahsilat_missing_prim" && n.entity_type !== "tahsilat_gonderme_bugun" && n.entity_type !== "personnel_added" && n.entity_type !== "advance_request").length} okunmamış mesaj · tıklanınca prim quick-view açılır
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="admin-notif-mark-all"
                onClick={markAllNotifsRead}
                className="inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 rounded-md px-2 py-1 text-xs font-semibold"
              >
                <MailOpen size={11} /> Hepsini Okundu
              </button>
              <button
                data-testid="admin-notif-collapse"
                onClick={() => setNotifsCollapsed((v) => !v)}
                className="inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-md px-2 py-1 text-xs font-semibold"
              >
                {notifsCollapsed ? "Göster" : "Gizle"}
              </button>
            </div>
          </div>
          {!notifsCollapsed && (
            <div className="p-2 space-y-1.5 max-h-56 overflow-y-auto">
              {notifs.filter((n) => !n.read_at && n.entity_type !== "tahsilat_missing_prim" && n.entity_type !== "tahsilat_gonderme_bugun" && n.entity_type !== "personnel_added" && n.entity_type !== "advance_request").slice(0, 10).map((n) => {
                const isAudit = n.type === "audit_mismatch";
                return (
                <div
                  key={n.id}
                  data-testid={`admin-notif-${n.id}`}
                  className={`${isAudit ? "bg-amber-50 border-amber-400" : "bg-white border-amber-200"} border rounded-md p-2 flex items-start gap-2 text-xs`}
                >
                  {isAudit ? (
                    <ShieldAlert size={14} className="text-amber-700 shrink-0 mt-0.5" />
                  ) : (
                    <MessageSquare size={13} className="text-amber-600 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-800 truncate flex items-center gap-1" title={n.prim_label}>
                      {isAudit && (
                        <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wider bg-amber-600 text-white rounded px-1 py-0.5">
                          AUDIT
                        </span>
                      )}
                      {n.prim_label}
                    </div>
                    <div className="text-slate-700 whitespace-pre-wrap leading-snug mt-0.5">
                      {isAudit ? n.note_text : `"${n.note_text}"`}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {n.from_email} · {(() => { try { return new Date(n.created_at).toLocaleString("tr-TR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }); } catch { return ""; } })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {isAudit ? (
                      <>
                        {(n.meta?.varlik_delta || 0) > 0 && (
                          <button
                            data-testid={`admin-notif-varlik-${n.id}`}
                            onClick={() => { setAuditJump("varlik"); markNotifRead(n.id); }}
                            className="inline-flex items-center justify-center gap-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                            title="Varlık Denetimi modalını aç"
                          >
                            <ShieldAlert size={10} /> V·{n.meta?.varlik_delta}
                          </button>
                        )}
                        {(n.meta?.giden_delta || 0) > 0 && (
                          <button
                            data-testid={`admin-notif-giden-${n.id}`}
                            onClick={() => { setAuditJump("giden"); markNotifRead(n.id); }}
                            className="inline-flex items-center justify-center gap-1 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                            title="Giden Kapsam Denetimi modalını aç"
                          >
                            <ShieldAlert size={10} /> G·{n.meta?.giden_delta}
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        data-testid={`admin-notif-open-${n.id}`}
                        onClick={() => setQuickView(n.prim_id)}
                        className="inline-flex items-center justify-center bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                        title="Prim detayını aç"
                      >
                        <Eye size={10} />
                      </button>
                    )}
                    <button
                      data-testid={`admin-notif-read-${n.id}`}
                      onClick={() => markNotifRead(n.id)}
                      className="inline-flex items-center justify-center bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                      title="Okundu olarak işaretle"
                    >
                      <MailOpen size={10} />
                    </button>
                  </div>
                </div>
                );
              })}
              {notifs.filter((n) => !n.read_at).length > 10 && (
                <div className="text-[10px] text-amber-700 font-semibold text-center py-1">
                  +{notifs.filter((n) => !n.read_at).length - 10} daha
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Admin sticky — eksik tahsilat primleri */}
      {isAdmin && notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_missing_prim").length > 0 && (
        <div
          data-testid="tahsilat-missing-sticky"
          className="sticky top-0 z-20 bg-rose-50 border-2 border-rose-400 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between gap-2 p-3 border-b border-rose-200 bg-rose-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-rose-500 text-white grid place-items-center relative">
                <AlertCircle size={14} />
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-white text-rose-700 rounded-full border border-rose-300 inline-flex items-center justify-center">
                  {notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_missing_prim").length}
                </span>
              </div>
              <div>
                <div className="font-display font-extrabold text-rose-900 text-sm">
                  Tahsilat Prim Eksikleri
                </div>
                <div className="text-[11px] text-rose-800 font-semibold uppercase tracking-wider">
                  Tahsilatta adı geçen ama prim girişi yapmamış personeller · "Prim Ekle" ile hızlıca kaydet
                </div>
              </div>
            </div>
          </div>
          <div className="p-2 space-y-1.5 max-h-56 overflow-y-auto">
            {notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_missing_prim").slice(0, 20).map((n) => (
              <div
                key={n.id}
                data-testid={`tahsilat-missing-${n.id}`}
                className="bg-white border border-rose-200 rounded-md p-2 flex items-start gap-2 text-xs"
              >
                <AlertCircle size={13} className="text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800">
                    {n.meta?.personnel_name || "Personel"} · {n.meta?.muvekkil || ""}
                  </div>
                  <div className="text-[11px] text-slate-700 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>Föy: <strong>{n.meta?.icra_foy}</strong></span>
                    <span>Dosya: <strong>{n.meta?.icra_esas}</strong></span>
                    <span>Vekalet: <strong className="text-emerald-700">{(n.meta?.vekalet_ucreti || 0).toLocaleString("tr-TR")} ₺</strong></span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`tahsilat-missing-create-${n.id}`}
                    onClick={async () => {
                      try {
                        const r = await api.get(`/tahsilat/missing-prim-prefill/${n.id}`);
                        const pre = r.data?.prefill || {};
                        setEditing({
                          id: null,
                          tarih: pre.tarih,
                          icra_foy: pre.icra_foy || "",
                          icra_esas: pre.icra_esas || "",
                          icra_dairesi: pre.icra_dairesi || "",
                          alacakli_adi: pre.alacakli_adi || "",
                          vekalet_ucreti: pre.vekalet_ucreti || 0,
                          tahsilat_miktari: pre.tahsilat_miktari || 0,
                          giden_kisiler: pre.giden_kisiler || [],
                          arastiran_kisi_id: pre.arastiran_kisi_id || null,
                          _prefill_notification_id: n.id,
                        });
                      } catch (e) {
                        toast.error("Prefill alınamadı");
                      }
                    }}
                    className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white rounded-md px-2 py-1 text-xs font-semibold"
                  >
                    + Prim Ekle
                  </button>
                  <button
                    onClick={() => markNotifRead(n.id)}
                    className="inline-flex items-center gap-1 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 rounded-md px-2 py-1 text-[10px] font-semibold"
                    title="Yoksay"
                  >
                    <X size={10} /> Yoksay
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin sticky — bugün gönderilecek tahsilatlar (cron) */}
      {isAdmin && notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_gonderme_bugun").length > 0 && (
        <div
          data-testid="tahsilat-gonderme-sticky"
          className="sticky top-0 z-20 bg-orange-50 border-2 border-orange-400 rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between gap-2 p-3 border-b border-orange-200 bg-orange-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500 text-white grid place-items-center relative">
                <Bell size={14} />
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-white text-orange-700 rounded-full border border-orange-300 inline-flex items-center justify-center">
                  {notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_gonderme_bugun").length}
                </span>
              </div>
              <div>
                <div className="font-display font-extrabold text-orange-900 text-sm">
                  Bugün Gönderilecek Tahsilatlar
                </div>
                <div className="text-[11px] text-orange-800 font-semibold uppercase tracking-wider">
                  Gönderme tarihi bugün olan tahsilatlar · "Satırı Aç" ile Tahsilat sekmesinde incele
                </div>
              </div>
            </div>
          </div>
          <div className="p-2 space-y-1.5 max-h-56 overflow-y-auto">
            {notifs.filter((n) => !n.read_at && n.entity_type === "tahsilat_gonderme_bugun").slice(0, 20).map((n) => (
              <div
                key={n.id}
                data-testid={`tahsilat-gonderme-${n.id}`}
                className="bg-white border border-orange-200 rounded-md p-2 flex items-start gap-2 text-xs"
              >
                <Bell size={13} className="text-orange-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800">
                    {n.meta?.muvekkil || "—"}
                  </div>
                  <div className="text-[11px] text-slate-700 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>Föy: <strong>{n.meta?.icra_foy || "-"}</strong></span>
                    <span>Dosya: <strong>{n.meta?.dosya_no || "-"}</strong></span>
                    <span>Tutar: <strong className="text-emerald-700">{Number(n.meta?.tutar || 0).toLocaleString("tr-TR")} ₺</strong></span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`tahsilat-gonderme-open-${n.id}`}
                    onClick={() => {
                      const tid = n.meta?.tahsilat_id;
                      if (!tid) { toast.error("Tahsilat kimliği bulunamadı"); return; }
                      try { sessionStorage.setItem("bordromat:tahsilat-highlight-pending", tid); } catch {}
                      window.dispatchEvent(new CustomEvent("bordromat:gotoTab", { detail: "tahsilat" }));
                      // Also dispatch highlight event (in case TahsilatTab already mounted)
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent("bordromat:tahsilat-highlight", { detail: tid }));
                      }, 50);
                      markNotifRead(n.id);
                    }}
                    className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-700 text-white rounded-md px-2 py-1 text-xs font-semibold whitespace-nowrap"
                    title="Tahsilat sekmesinde satırı aç"
                  >
                    <ChevronRight size={11} /> Satırı Aç
                  </button>
                  <button
                    onClick={() => markNotifRead(n.id)}
                    className="inline-flex items-center gap-1 bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-md px-2 py-1 text-[10px] font-semibold"
                    title="Yoksay"
                  >
                    <X size={10} /> Yoksay
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Muhasebeci ön onay bekleyen admin akışı */}
      {(() => {
        const preApproved = rows.filter((r) => r.muhasebeci_onay);
        if (preApproved.length === 0) return null;
        return (
          <div
            data-testid="pending-preapproved-banner"
            className="bg-sky-50 border-l-4 border-sky-500 border border-sky-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2 text-sm text-sky-900">
              <div className="w-8 h-8 rounded-lg bg-sky-500 text-white grid place-items-center shrink-0">
                <CheckCircle2 size={14} />
              </div>
              <div>
                <div className="font-display font-bold text-sky-900 leading-tight">
                  {preApproved.length} prim muhasebeci tarafından ön onaylandı
                </div>
                <div className="text-[11px] text-sky-700 font-semibold">
                  {isAdmin
                    ? "Final onayınız bekleniyor — listede en üstte sabitlendi"
                    : "Admin'in final onayı bekleniyor"}
                </div>
              </div>
            </div>
            {isAdmin && (
              <button
                data-testid="pending-select-preapproved"
                onClick={() => setSelected(new Set(preApproved.map((r) => r.id)))}
                className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-md px-3 py-1.5 text-xs font-semibold"
              >
                <CheckCircle2 size={12} /> Hepsini Seç ({preApproved.length})
              </button>
            )}
          </div>
        );
      })()}

      {/* Conflict (mükerrer föy+dosya) banner */}
      {conflictGroupCount > 0 && (
        <div
          data-testid="pending-conflict-banner"
          className="bg-fuchsia-50 border border-fuchsia-300 rounded-lg p-3 text-xs text-fuchsia-900 flex flex-wrap items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2">
            <GitMerge size={14} />
            <span>
              <strong>{conflictGroupCount}</strong> mükerrer föy+dosya grubu bulundu — birden fazla personel aynı dosyaya prim eklemiş.
              Aynı renkli satırları seçip <strong>Birleştir</strong> ile tek primde toplayabilirsiniz.
            </span>
          </div>
          <button
            data-testid="pending-select-first-conflict"
            onClick={() => {
              const firstGroupId = sortedRows.find((r) => r.has_conflict)?.conflict_group_id;
              if (!firstGroupId) return;
              setSelected(new Set(rows.filter((r) => r.conflict_group_id === firstGroupId).map((r) => r.id)));
            }}
            className="inline-flex items-center gap-1 bg-white border border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-100 rounded-md px-2 py-0.5 font-semibold"
          >
            İlk Grubu Seç
          </button>
        </div>
      )}

      {/* Bu ay merge istatistikleri (personel bazında) */}
      {stats && stats.total_merges > 0 && (
        <div
          data-testid="pending-merge-stats"
          className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs"
        >
          <button
            type="button"
            onClick={() => setStatsOpen((s) => !s)}
            data-testid="pending-merge-stats-toggle"
            className="w-full flex items-center justify-between gap-2 font-semibold text-emerald-900"
          >
            <span className="inline-flex items-center gap-2">
              <BarChart3 size={14} />
              Bu ay <strong>{stats.total_merges}</strong> çakışan föy birleştirildi
              · <strong>{stats.total_primler_merged}</strong> prim konsolide oldu
              {stats.per_personel?.length > 0 && (
                <span className="opacity-80">· {stats.per_personel.length} personel etkilendi</span>
              )}
            </span>
            <span className="text-[10px] opacity-60">{statsOpen ? "▲ gizle" : "▼ personel detayı"}</span>
          </button>
          {statsOpen && stats.per_personel?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-emerald-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="pending-merge-stats-details">
              {stats.per_personel.map((p) => (
                <div key={p.personnel_id} className="bg-white rounded-md border border-emerald-200 px-2.5 py-1.5 flex items-center justify-between" data-testid={`merge-stat-${p.personnel_id}`}>
                  <div className="truncate">
                    <div className="font-bold text-slate-800 text-xs truncate" title={p.ad_soyad}>{p.ad_soyad || "—"}</div>
                    <div className="text-[10px] text-slate-500">{p.primler_merged} prim konsolide</div>
                  </div>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold font-mono-tab">
                    {p.merge_count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stale warning banner */}
      {staleRows.length > 0 && (
        <div
          data-testid="pending-stale-banner"
          className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 flex flex-wrap items-center justify-between gap-2"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} />
            <span>
              <strong>{staleRows.length}</strong> prim <strong>{STALE_DAYS}+ gün</strong> onaysız bekliyor — personel ödemeleri gecikmesin
            </span>
          </div>
          <button
            data-testid="pending-select-stale"
            onClick={() => setSelected(new Set(staleRows.map((r) => r.id)))}
            className="inline-flex items-center gap-1 bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 rounded-md px-2 py-0.5 font-semibold"
          >
            Sadece Bu {staleRows.length} Kaydı Seç
          </button>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        Onay bekleyen primler <strong>maaş bordrosunda görünmez</strong>. Onayladığınız anda ilgili ayın bordrosuna otomatik yansır.
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm sticky-table">
        <SortProvider value={colSort}>
        <table className="w-full text-sm" data-testid="pending-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              <th className="px-3 py-3 w-10">
                <input
                  data-testid="pending-select-all"
                  type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-indigo-600 cursor-pointer"
                />
              </th>
              <STh k="tarih" className="px-3 py-3">Tarih</STh>
              <STh k="dosya_adi" className="px-3 py-3">Dosya</STh>
              <STh k="alacakli_adi" className="px-3 py-3">Müvekkil</STh>
              <STh k="tahsilat_miktari" className="px-3 py-3 text-right">Tahsilat</STh>
              <STh k="vekalet_ucreti" className="px-3 py-3 text-right">Vekalet</STh>
              {canSeePrimRate && <STh k="odenecek_toplam" className="px-3 py-3 text-right">Ödenecek</STh>}
              <STh k="giden" className="px-3 py-3">Giden Kişiler</STh>
              <STh k="giren" className="px-3 py-3">Giren</STh>
              <STh k="not" className="px-3 py-3 text-center">Not</STh>
              <th className="px-3 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-10 text-slate-400">Yükleniyor...</td></tr>}
            {!loading && sortedRows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-emerald-600 text-sm font-semibold">
                <CheckCircle2 size={32} className="mx-auto mb-2 opacity-70" />
                Onay bekleyen prim yok · Her şey güncel
              </td></tr>
            )}
            {!loading && sortedRows.length > 0 && displayRows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-6 text-slate-400 text-sm" data-testid="pending-filter-empty">
                Bu filtreye uyan kayıt yok
              </td></tr>
            )}
            {colSort.sorted.map((r) => {
              const age = daysSince(r.created_at);
              const stale = age >= STALE_DAYS;
              const inConflict = r.has_conflict && r.conflict_group_id;
              const confColor = inConflict ? CONFLICT_COLORS[(r.conflict_group_id - 1) % CONFLICT_COLORS.length] : "";
              const preApproved = !!r.muhasebeci_onay;
              return (
              <tr
                key={r.id}
                data-testid={`pending-row-${r.id}`}
                data-conflict-group={inConflict ? r.conflict_group_id : undefined}
                data-preapproved={preApproved ? "1" : undefined}
                className={`border-b border-slate-100 ${
                  preApproved
                    ? "bg-sky-50/60 hover:bg-sky-50 border-l-4 border-l-sky-500"
                    : inConflict
                    ? `${confColor} border-l-4 border-l-fuchsia-500`
                    : stale ? "bg-rose-50/50 hover:bg-rose-50" : "hover:bg-slate-50"
                }`}
              >
                <td className="px-3 py-2.5">
                  <input
                    data-testid={`pending-check-${r.id}`}
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"
                  />
                </td>
                <td className="px-3 py-2.5 font-mono-tab text-xs">
                  {fmtDate(r.tarih)}
                  {inConflict && (
                    <div
                      data-testid={`pending-conflict-badge-${r.id}`}
                      className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-300"
                      title={`Bu föy+dosya ile ${r.conflict_count} prim var — birleştir`}
                    >
                      <GitMerge size={8} /> {r.conflict_count}×
                    </div>
                  )}
                  {(() => {
                    const h = r.historical_dup || {};
                    const total = (h.foy_count || 0) + (h.esas_count || 0);
                    if (total === 0) return null;
                    const parts = [];
                    (h.foy_matches || []).forEach((m) => parts.push(`Föy: ${fmtDate(m.tarih)} · ${m.dosya_adi || "—"}${m.onaylandi ? " (onaylı)" : ""}`));
                    (h.esas_matches || []).forEach((m) => parts.push(`Esas: ${fmtDate(m.tarih)} · ${m.dosya_adi || "—"}${m.onaylandi ? " (onaylı)" : ""}`));
                    const first = (h.foy_matches || [])[0] || (h.esas_matches || [])[0];
                    return (
                      <button
                        type="button"
                        data-testid={`pending-hist-badge-${r.id}`}
                        onClick={() => first && setQuickView(first.id)}
                        className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors"
                        title={`Önceki aylarda ${total} kez kullanılmış · tıkla ilk eşleşmeyi aç\n${parts.join("\n")}`}
                      >
                        <AlertTriangle size={8} /> Önceden {total}×
                      </button>
                    );
                  })()}
                  {stale && (
                    <div
                      data-testid={`pending-stale-badge-${r.id}`}
                      className="inline-flex items-center gap-0.5 ml-1.5 px-1.5 py-0 rounded-full text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200"
                      title={`${age} gündür onay bekliyor`}
                    >
                      <AlertTriangle size={8} /> {age} gün
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-semibold">
                  {r.dosya_adi || "—"}
                  {inConflict && (
                    <span className="block text-[9px] font-normal text-fuchsia-700 mt-0.5">
                      Föy: {r.icra_foy || "—"}
                    </span>
                  )}
                  <TahsilatMatchNote primId={r.id} match={matches[r.id]} onApplied={load} />
                </td>
                <td className="px-3 py-2.5">{r.alacakli_adi || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono-tab">{formatNum(r.tahsilat_miktari)}</td>
                <td className="px-3 py-2.5 text-right font-mono-tab">{formatNum(r.vekalet_ucreti)}</td>
                {canSeePrimRate && <td className="px-3 py-2.5 text-right font-mono-tab font-semibold text-emerald-700">{formatNum(r.odenecek_toplam)}</td>}
                <td className="px-3 py-2.5 text-xs text-slate-600">{(r.giden_isimleri || []).filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold capitalize">{r.created_by_role || "—"}</span>
                    <span className="opacity-70 truncate max-w-[160px]">{r.created_by_email || ""}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <MuhasebeciNoteCell
                    row={r}
                    open={notePopover === r.id}
                    onOpen={() => setNotePopover(r.id)}
                    onClose={() => setNotePopover(null)}
                    onSaved={() => { setNotePopover(null); load(); }}
                  />
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {r.muhasebeci_onay && (
                      <span
                        data-testid={`pending-preapproved-${r.id}`}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-300"
                        title={`Ön onay: ${r.muhasebeci_onay.by_email} · ${r.muhasebeci_onay.at ? new Date(r.muhasebeci_onay.at).toLocaleString("tr-TR") : ""}`}
                      >
                        <CheckCircle2 size={9} /> Ön Onay
                      </span>
                    )}
                    <button
                      data-testid={`pending-edit-${r.id}`}
                      onClick={() => setEditing(r)}
                      className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                      title="Pending primi düzenle"
                    >
                      <Edit size={12} />
                    </button>
                    <button
                      data-testid={`pending-preapprove-${r.id}`}
                      onClick={() => toggleMuhasebeciOnay(r)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold ${
                        r.muhasebeci_onay
                          ? "bg-sky-600 hover:bg-sky-700 text-white"
                          : "bg-sky-100 hover:bg-sky-200 text-sky-800"
                      }`}
                      title={r.muhasebeci_onay
                        ? "Ön onayı geri al"
                        : "Ön onayla — admin'e final onay için hazır olarak işaretle"}
                    >
                      <CheckCircle2 size={12} /> {r.muhasebeci_onay ? "Ön Onay ✓" : "Ön Onay"}
                    </button>
                    <button
                      data-testid={`pending-reject-${r.id}`}
                      onClick={() => rejectOne(r)}
                      className="inline-flex items-center gap-1 bg-rose-50 hover:bg-rose-100 text-rose-700 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                      title="Reddet · personele bildirim gider"
                    >
                      <X size={12} /> Reddet
                    </button>
                    {isAdmin && !r.onaylandi && (
                      <button
                        data-testid={`pending-approve-${r.id}`}
                        onClick={() => approveOne(r.id)}
                        className="inline-flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                      >
                        <Check size={12} /> Onayla
                      </button>
                    )}
                    {isAdmin && r.onaylandi && (
                      <button
                        data-testid={`pending-unapprove-${r.id}`}
                        onClick={() => unapproveOne(r)}
                        className="inline-flex items-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-800 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                        title="Onayı kaldır — tekrar pending durumuna döner"
                      >
                        <RotateCcw size={12} /> Onayı Kaldır
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </SortProvider>
      </div>
      {rejectDialog && (
        <RejectReasonDialog
          mode={rejectDialog.mode}
          count={rejectDialog.ids.length}
          ids={rejectDialog.ids}
          row={rejectDialog.row}
          primRows={rows}
          adminEmail={user?.email || ""}
          templates={templates}
          isAdmin={isAdmin}
          onReloadTemplates={reloadTemplates}
          onCancel={() => setRejectDialog(null)}
          onSubmit={submitReject}
        />
      )}
      {blockDialog && (
        <ApproveBlockDialog
          state={blockDialog}
          onCancel={() => setBlockDialog(null)}
          onOverride={confirmOverrideApprove}
          onOpenQuickView={(id) => setQuickView(id)}
          onMergeComplete={confirmMergeInto}
        />
      )}
      {quickView && (
        <PrimQuickViewModal
          primId={quickView}
          onClose={() => setQuickView(null)}
        />
      )}
      {editing && (
        <PrimModal
          key={editing.id || "new-prim"}
          editing={editing}
          personnel={personnel}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {auditJump === "varlik" && (
        <VarlikAuditModal
          onClose={() => setAuditJump(null)}
          onFixed={() => { reloadNotifs(); }}
        />
      )}
      {auditJump === "giden" && (
        <GidenScopeAuditModal
          onClose={() => setAuditJump(null)}
          onFixed={() => { reloadNotifs(); }}
        />
      )}
    </div>
  );
}

// =========================
// RejectReasonDialog — AI-suggested top-3 + chip templates + AI draft note + manager
// =========================
function RejectReasonDialog({ mode, count, ids, row, primRows, adminEmail, templates, isAdmin, onReloadTemplates, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [appliedIds, setAppliedIds] = useState([]); // in-order (dedup)
  const [managerOpen, setManagerOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]); // [{template_id, reason, score}]
  const [aiError, setAiError] = useState("");
  const [suggestionId, setSuggestionId] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [perPersonelMode, setPerPersonelMode] = useState(false);
  const [perPersonelLoading, setPerPersonelLoading] = useState(false);
  const [perPersonelNotes, setPerPersonelNotes] = useState([]); // [{personnel_id, personnel_name, draft, prim_count}]
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchAiSuggestions = async () => {
    if (!ids || ids.length === 0) return;
    setAiLoading(true);
    setAiError("");
    try {
      const r = await api.post("/prim/suggest-templates", { ids });
      setAiSuggestions(r.data?.suggestions || []);
      setSuggestionId(r.data?.suggestion_id || null);
      if (!r.data?.suggestions?.length) setAiError("AI önerisi yüklenemedi");
    } catch (e) {
      setAiSuggestions([]);
      setSuggestionId(null);
      setAiError("AI önerisi yüklenemedi");
    } finally {
      setAiLoading(false);
    }
  };

  const generateDraft = async () => {
    if (!ids || ids.length === 0) return;
    setDraftLoading(true);
    try {
      const r = await api.post("/prim/draft-reject-note", {
        ids,
        template_ids: appliedIds,
      });
      const draft = (r.data?.draft || "").trim();
      if (!draft) {
        toast.error("AI reddet notu oluşturamadı — tekrar deneyin");
        return;
      }
      setReason(draft);
      toast.success("AI reddet notu oluşturuldu · düzenleyebilirsiniz");
    } catch (e) {
      toast.error("Not oluşturulamadı");
    } finally {
      setDraftLoading(false);
    }
  };

  const generatePerPersonelDrafts = async () => {
    if (!ids || ids.length === 0) return;
    setPerPersonelLoading(true);
    try {
      const r = await api.post("/prim/draft-reject-notes-per-personel", {
        ids,
        template_ids: appliedIds,
      });
      const notes = r.data?.personel_notes || [];
      if (!notes.length) {
        toast.error("Giden personel bulunamadı");
        return;
      }
      setPerPersonelNotes(notes);
      setPerPersonelMode(true);
      const okCount = notes.filter((n) => n.draft).length;
      toast.success(`${okCount}/${notes.length} personel için kişisel not oluşturuldu`);
    } catch (e) {
      toast.error("Kişisel notlar oluşturulamadı");
    } finally {
      setPerPersonelLoading(false);
    }
  };

  const updatePerPersonelNote = (pid, newDraft) => {
    setPerPersonelNotes((cur) => cur.map((n) =>
      n.personnel_id === pid ? { ...n, draft: newDraft } : n
    ));
  };

  const exitPerPersonelMode = () => {
    setPerPersonelMode(false);
  };

  // Fire AI suggestions once on open
  useEffect(() => {
    fetchAiSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build suggestion lookup + reorder templates (suggested first in given order, then rest unchanged)
  const suggestionMap = useMemo(() => {
    const m = new Map();
    aiSuggestions.forEach((s, idx) => m.set(s.template_id, { ...s, rank: idx + 1 }));
    return m;
  }, [aiSuggestions]);

  const orderedTemplates = useMemo(() => {
    if (suggestionMap.size === 0) return templates;
    const picked = [];
    const seen = new Set();
    aiSuggestions.forEach((s) => {
      const t = templates.find((x) => x.id === s.template_id);
      if (t && !seen.has(t.id)) { picked.push(t); seen.add(t.id); }
    });
    const rest = templates.filter((t) => !seen.has(t.id));
    return [...picked, ...rest];
  }, [templates, aiSuggestions, suggestionMap]);

  const title = mode === "bulk" ? `${count} prim toplu reddedilsin mi?` : `Prim reddedilsin mi?`;
  const subtitle = mode === "bulk"
    ? "Seçili pending primler silinecek. Personel'lere bırakacağınız kısa not (opsiyonel):"
    : (row ? `"${row.alacakli_adi || row.dosya_adi}" primi silinecek. Personel'e not (opsiyonel):` : "");
  const applyTemplate = (t) => {
    setReason((cur) => cur.trim() ? `${cur.trim()}\n${t.text}` : t.text);
    setAppliedIds((cur) => cur.includes(t.id) ? cur : [...cur, t.id]);
  };
  // Preview messages — mirror of what personel will see in their inbox
  const previewMessages = useMemo(() => {
    const affectedPrims = mode === "single"
      ? (row ? [row] : [])
      : (primRows || []).filter((r) => (ids || []).includes(r.id));
    const perPidMap = {};
    if (perPersonelMode) {
      perPersonelNotes.forEach((n) => {
        const t = (n.draft || "").trim();
        if (t) perPidMap[n.personnel_id] = t;
      });
    }
    const fallback = (reason || "").trim();
    const nowIso = new Date().toISOString();
    const msgs = [];
    affectedPrims.forEach((p) => {
      const label = [p.alacakli_adi, p.icra_foy, p.tarih].filter(Boolean).join(" · ");
      const gids = p.giden_kisiler || [];
      const gnames = p.giden_isimleri || [];
      gids.forEach((pid, idx) => {
        if (!pid) return;
        const text = (perPidMap[pid] || fallback || "").trim();
        if (!text) return; // empty → no notification
        msgs.push({
          id: `${p.id}-${pid}`,
          prim_id: p.id,
          prim_label: label,
          message: text,
          from_admin_email: adminEmail || "yönetici",
          created_at: nowIso,
          personnel_name: gnames[idx] || "",
          personnel_id: pid,
        });
      });
    });
    return msgs;
  }, [mode, row, primRows, ids, perPersonelMode, perPersonelNotes, reason, adminEmail]);

  const previewSilentPersonel = useMemo(() => {
    // In per-personel mode, which personels got no notification (empty draft and no fallback)
    if (!perPersonelMode) return [];
    const fallback = (reason || "").trim();
    if (fallback) return []; // fallback covers them
    return perPersonelNotes
      .filter((n) => !(n.draft || "").trim())
      .map((n) => n.personnel_name || n.personnel_id);
  }, [perPersonelMode, perPersonelNotes, reason]);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      let personelNotes = null;
      if (perPersonelMode && perPersonelNotes.length > 0) {
        personelNotes = {};
        perPersonelNotes.forEach((n) => {
          const t = (n.draft || "").trim();
          if (t) personelNotes[n.personnel_id] = t;
        });
      }
      await onSubmit(reason, appliedIds, suggestionId, personelNotes);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl my-8" data-testid="reject-dialog">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2 text-rose-800">
              <X size={18} /> {title}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <button
            data-testid="reject-dialog-close"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"
          ><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {/* AI Öneri Durum Satırı */}
          <div
            data-testid="ai-suggest-status"
            className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-[11px] font-semibold border ${
              aiLoading
                ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                : aiSuggestions.length > 0
                ? "bg-violet-50 border-violet-200 text-violet-900"
                : "bg-slate-50 border-slate-200 text-slate-600"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles size={12} className={aiLoading ? "animate-pulse" : ""} />
              {aiLoading
                ? "AI uygun şablonları belirliyor..."
                : aiSuggestions.length > 0
                ? `AI Önerisi · prim verilerine göre top ${aiSuggestions.length} şablon öne çıkarıldı`
                : (aiError || "AI önerisi mevcut değil")}
            </span>
            <button
              type="button"
              data-testid="ai-suggest-refresh"
              onClick={fetchAiSuggestions}
              disabled={aiLoading}
              className="inline-flex items-center gap-1 bg-white border border-current/20 rounded px-1.5 py-0.5 hover:bg-white/70 disabled:opacity-50"
              title="AI önerisini yenile"
            >
              <RefreshCw size={10} className={aiLoading ? "animate-spin" : ""} />
            </button>
          </div>

          {orderedTemplates.length > 0 && (
            <div data-testid="reject-templates">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={11} /> Hazır Şablonlar · {suggestionMap.size > 0 ? "AI önerileri önde" : "en çok kullanılanlar başta"}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    data-testid="reject-templates-manage"
                    onClick={() => setManagerOpen(true)}
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold normal-case tracking-normal"
                  >
                    <Settings size={11} /> Yönet
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {orderedTemplates.map((t) => {
                  const applied = appliedIds.includes(t.id);
                  const usage = t.usage_count || 0;
                  const sug = suggestionMap.get(t.id);
                  const isSuggested = !!sug;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`reject-template-${t.id}`}
                      data-ai-suggested={isSuggested ? "1" : undefined}
                      onClick={() => applyTemplate(t)}
                      className={`inline-flex items-center gap-1 border rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                        applied
                          ? "bg-rose-600 text-white border-rose-600"
                          : isSuggested
                          ? "bg-violet-50 hover:bg-violet-100 border-violet-400 text-violet-900 ring-1 ring-violet-200"
                          : "bg-rose-50 hover:bg-rose-100 border-rose-200 text-rose-800"
                      }`}
                      title={isSuggested
                        ? `AI #${sug.rank} · ${sug.reason || "önerildi"} · [${t.category || "diger"}] ${t.text}`
                        : `[${t.category || "diger"}] ${t.text}`}
                    >
                      {isSuggested && !applied && (
                        <Sparkles size={10} className="text-violet-600" />
                      )}
                      {applied ? "✓" : "+"} {t.label}
                      {usage > 0 && (
                        <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold font-mono-tab ${
                          applied ? "bg-white text-rose-700" : isSuggested ? "bg-violet-200 text-violet-900" : "bg-rose-200 text-rose-900"
                        }`}>
                          {usage}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {aiSuggestions.length > 0 && (
                <div className="mt-1.5 text-[10px] text-violet-700 italic" data-testid="ai-suggest-reasons">
                  {aiSuggestions.slice(0, 3).map((s, i) => {
                    const t = templates.find((x) => x.id === s.template_id);
                    if (!t) return null;
                    return (
                      <div key={s.template_id} className="truncate" title={s.reason}>
                        <span className="font-bold">#{i + 1} {t.label}</span>
                        {s.reason ? <span> — {s.reason}</span> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!perPersonelMode && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center justify-between gap-2">
                <span>Not {reason.trim() ? "" : "(boş bırakılırsa bildirim gönderilmez)"}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    data-testid="ai-draft-note-btn"
                    onClick={generateDraft}
                    disabled={draftLoading || busy || perPersonelLoading}
                    className="inline-flex items-center gap-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal transition-colors"
                    title="AI ile prim verisine uygun TR reddet notu oluştur"
                  >
                    <Sparkles size={10} className={draftLoading ? "animate-pulse" : ""} />
                    {draftLoading ? "Oluşturuluyor..." : "AI ile Not Oluştur"}
                  </button>
                  {mode === "bulk" && (
                    <button
                      type="button"
                      data-testid="ai-draft-per-personel-btn"
                      onClick={generatePerPersonelDrafts}
                      disabled={perPersonelLoading || busy || draftLoading}
                      className="inline-flex items-center gap-1 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-white rounded px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal transition-colors"
                      title="Her giden personel için ayrı AI reddet notu oluştur (ismiyle)"
                    >
                      <Sparkles size={10} className={perPersonelLoading ? "animate-pulse" : ""} />
                      {perPersonelLoading ? "Hazırlanıyor..." : "AI Kişisel Not"}
                    </button>
                  )}
                </div>
              </div>
              <textarea
                data-testid="reject-reason-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Örn: İcra Esas numarası eksik, düzeltip tekrar gönderin."
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400"
              />
            </div>
          )}

          {perPersonelMode && (
            <div data-testid="per-personel-notes-panel">
              <div className="text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 mb-1.5 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5">
                  <Sparkles size={11} /> AI Kişisel Notlar · {perPersonelNotes.length} personel
                </span>
                <button
                  type="button"
                  data-testid="per-personel-exit-btn"
                  onClick={exitPerPersonelMode}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold normal-case tracking-normal"
                  title="Tek ortak nota geri dön"
                >
                  <X size={10} /> Tek nota dön
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {perPersonelNotes.map((n) => (
                  <div
                    key={n.personnel_id}
                    data-testid={`per-personel-row-${n.personnel_id}`}
                    className="border border-fuchsia-200 bg-fuchsia-50/40 rounded-md p-2"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-fuchsia-900">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold font-mono-tab">
                          {n.personnel_name?.slice(0, 1).toUpperCase() || "?"}
                        </span>
                        {n.personnel_name || "—"}
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-fuchsia-700 opacity-80">
                          · {n.prim_count} prim
                        </span>
                      </div>
                      {!n.draft && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
                          Oluşturulamadı
                        </span>
                      )}
                    </div>
                    <textarea
                      data-testid={`per-personel-textarea-${n.personnel_id}`}
                      value={n.draft || ""}
                      onChange={(e) => updatePerPersonelNote(n.personnel_id, e.target.value)}
                      rows={2}
                      placeholder={`Sayın ${n.personnel_name}, ... (boş bırakılırsa bu kişiye bildirim gitmez)`}
                      className="w-full border border-fuchsia-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-fuchsia-200 focus:border-fuchsia-400 bg-white"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-1.5 text-[10px] text-fuchsia-700 italic">
                Her personel SADECE kendi notunu alır. Boş notlu personele bildirim gitmez.
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            data-testid="reject-dialog-cancel"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >İptal</button>
          <button
            type="button"
            data-testid="reject-dialog-preview"
            onClick={() => setPreviewOpen(true)}
            disabled={busy || previewMessages.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            title={previewMessages.length === 0
              ? "Önizlenecek bildirim yok — not yazın veya kişisel not oluşturun"
              : `${previewMessages.length} personel'in göreceği bildirimi önizle`}
          >
            <Eye size={14} /> Önizle · {previewMessages.length}
          </button>
          <button
            data-testid="reject-dialog-submit"
            onClick={handleSubmit}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {busy ? "Reddediliyor..." : (
              perPersonelMode
                ? `Reddet + Kişisel Bildir (${perPersonelNotes.filter(n => (n.draft || "").trim()).length})`
                : (reason.trim() ? "Reddet + Bildir" : "Reddet (not yok)")
            )}
          </button>
        </div>
      </div>
      {managerOpen && (
        <RejectTemplatesManager
          templates={templates}
          onClose={() => setManagerOpen(false)}
          onChanged={onReloadTemplates}
        />
      )}
      {previewOpen && (
        <InboxPreviewModal
          messages={previewMessages}
          silentPersonel={previewSilentPersonel}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// =========================
// InboxPreviewModal — "personel inbox'ında şöyle görünecek" canlı önizleme
// =========================
function InboxPreviewModal({ messages, silentPersonel, onClose }) {
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    } catch { return ""; }
  };
  // Group messages by personnel so admin sees "what each personel will receive"
  const grouped = useMemo(() => {
    const by = new Map();
    messages.forEach((m) => {
      const key = m.personnel_id || m.personnel_name || "?";
      if (!by.has(key)) by.set(key, { personnel_name: m.personnel_name || "—", personnel_id: m.personnel_id, msgs: [] });
      by.get(key).msgs.push(m);
    });
    return Array.from(by.values()).sort((a, b) => (b.msgs.length - a.msgs.length));
  }, [messages]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="inbox-preview-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2 text-sky-800">
              <Eye size={18} /> Bildirim Önizlemesi
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Personel'in Prim ekranında <strong>birebir böyle</strong> görecek · reddet'ten vazgeçmediyseniz gönderilir
            </p>
          </div>
          <button
            data-testid="inbox-preview-close"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"
          ><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 bg-slate-50/40">
          {messages.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-8" data-testid="inbox-preview-empty">
              Önizlenecek bildirim yok — not boş bırakıldığından veya kişisel notlar oluşturulmadığından hiçbir personel'e mesaj gitmeyecek.
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.personnel_id || g.personnel_name} data-testid={`inbox-preview-group-${g.personnel_id || g.personnel_name}`}>
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold font-mono-tab">
                  {g.personnel_name?.slice(0, 1).toUpperCase() || "?"}
                </span>
                <span>{g.personnel_name}</span>
                <span className="text-[10px] text-slate-400 normal-case tracking-normal">· {g.msgs.length} bildirim</span>
              </div>
              {/* Mimic PersonelInboxBanner banner for a single-recipient view */}
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="relative w-7 h-7 rounded-md bg-rose-500 text-white grid place-items-center">
                      <Bell size={14} />
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] text-[10px] font-bold bg-white text-rose-600 rounded-full border border-rose-200 inline-flex items-center justify-center px-1">
                        {g.msgs.length}
                      </span>
                    </div>
                    <div>
                      <div className="font-display text-sm font-extrabold text-rose-900 leading-tight">
                        Yönetici Mesajları
                      </div>
                      <div className="text-[11px] text-rose-700 font-semibold uppercase tracking-wider">
                        {g.msgs.length} okunmamış · reddedilen primlerle ilgili geri bildirim
                      </div>
                    </div>
                  </div>
                  <button
                    disabled
                    className="inline-flex items-center gap-1 bg-white border border-rose-200 text-rose-400 rounded-md px-2 py-1 text-[11px] font-semibold cursor-not-allowed"
                    title="Önizleme — personel kendisi tıklar"
                  >
                    <MailOpen size={11} /> Hepsini Okundu
                  </button>
                </div>
                <div className="space-y-1.5">
                  {g.msgs.slice(0, 5).map((m) => (
                    <div
                      key={m.id}
                      data-testid={`inbox-preview-msg-${m.id}`}
                      className="bg-white border border-rose-200 rounded-md px-2.5 py-1.5 flex items-start gap-2 text-xs"
                    >
                      <X size={14} className="text-rose-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 truncate" title={m.prim_label}>
                          {m.prim_label || "Prim"}
                        </div>
                        <div className="text-slate-700 whitespace-pre-wrap leading-snug">
                          "{m.message}"
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5">
                          <span>{m.from_admin_email || "yönetici"}</span>
                          <span>·</span>
                          <span>{fmt(m.created_at)}</span>
                        </div>
                      </div>
                      <button disabled className="text-slate-300 shrink-0 cursor-not-allowed" title="Önizleme">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                  {g.msgs.length > 5 && (
                    <div className="text-[10px] text-rose-700 font-semibold text-center">
                      +{g.msgs.length - 5} daha
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {silentPersonel && silentPersonel.length > 0 && (
            <div
              data-testid="inbox-preview-silent"
              className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[11px] text-amber-900"
            >
              <div className="font-bold uppercase tracking-wider mb-0.5 flex items-center gap-1">
                <AlertTriangle size={11} /> Bildirim ALMAYACAK personel ({silentPersonel.length})
              </div>
              <div className="opacity-90">
                {silentPersonel.join(", ")} · ortak not boş + kişisel not girilmemiş
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            data-testid="inbox-preview-close-btn"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold"
          >
            Tamam, dialog'a dön
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================
// RejectTemplatesManager — admin add/edit/archive/import modal with category tabs
// =========================
function RejectTemplatesManager({ templates, onClose, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [newForm, setNewForm] = useState({ label: "", text: "", category: "diger" });
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // 'all' | category_id | 'archived'
  const [allTemplates, setAllTemplates] = useState(templates); // includes archived
  const [categories, setCategories] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/prim/reject-templates?include_archived=true");
      setAllTemplates(r.data?.rows || []);
      setCategories(r.data?.categories || []);
      onChanged && onChanged();
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = allTemplates.filter((t) => {
    if (activeTab === "all") return !t.archived;
    if (activeTab === "archived") return !!t.archived;
    return !t.archived && t.category === activeTab;
  });

  const tabs = [
    { id: "all", label: "Tümü", count: allTemplates.filter((t) => !t.archived).length },
    ...categories.map((c) => ({
      id: c.id,
      label: c.label,
      count: allTemplates.filter((t) => !t.archived && t.category === c.id).length,
    })),
    { id: "archived", label: "Arşiv", count: allTemplates.filter((t) => t.archived).length },
  ];

  const create = async () => {
    const label = newForm.label.trim();
    const text = newForm.text.trim();
    if (!label || !text) { toast.error("Başlık ve metin zorunlu"); return; }
    setBusy(true);
    try {
      await api.post("/prim/reject-templates", { label, text, category: newForm.category });
      toast.success("Şablon eklendi");
      setNewForm({ label: "", text: "", category: "diger" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    const label = editing.label.trim();
    const text = editing.text.trim();
    if (!label || !text) { toast.error("Başlık ve metin zorunlu"); return; }
    setBusy(true);
    try {
      await api.put(`/prim/reject-templates/${editing.id}`, {
        label, text, category: editing.category || "diger",
      });
      toast.success("Şablon güncellendi");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncelleme başarısız");
    } finally { setBusy(false); }
  };

  const archive = async (t) => {
    if (!window.confirm(`"${t.label}" şablonu arşivlensin mi?\nChip listesinden kalkar ama kullanım geçmişi korunur.`)) return;
    setBusy(true);
    try {
      await api.post(`/prim/reject-templates/${t.id}/archive`);
      toast.success("Arşivlendi");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arşivlenemedi");
    } finally { setBusy(false); }
  };

  const unarchive = async (t) => {
    setBusy(true);
    try {
      await api.post(`/prim/reject-templates/${t.id}/unarchive`);
      toast.success("Geri yüklendi");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yüklenemedi");
    } finally { setBusy(false); }
  };

  const removeHard = async (t) => {
    if (!window.confirm(`"${t.label}" şablonu KALICI olarak silinsin mi? (built-in değil)`)) return;
    setBusy(true);
    try {
      await api.delete(`/prim/reject-templates/${t.id}`);
      toast.success("Kalıcı silindi");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally { setBusy(false); }
  };

  const CATEGORY_COLORS = {
    eksik_bilgi: "bg-amber-100 text-amber-800 border-amber-200",
    kalite:      "bg-sky-100 text-sky-800 border-sky-200",
    mukerrer:    "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    diger:       "bg-slate-100 text-slate-700 border-slate-200",
  };
  const catLabel = (id) => categories.find((c) => c.id === id)?.label || id;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl my-8" data-testid="reject-templates-manager">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Settings size={18} /> Reddet Şablonları Yönetimi
          </h3>
          <div className="flex items-center gap-2">
            <button
              data-testid="templates-manager-import"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-200 rounded-md px-2.5 py-1.5 text-xs font-bold"
            ><Plus size={12} /> İçe Aktar</button>
            <button
              data-testid="templates-manager-close"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"
            ><X size={16} /></button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-5 pt-3 border-b border-slate-100 flex flex-wrap gap-1" data-testid="templates-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`templates-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-t-md text-xs font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-slate-900 text-slate-900 bg-slate-50"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
              } ${tab.id === "archived" ? "ml-auto" : ""}`}
            >
              {tab.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9px] font-bold font-mono-tab ${
                activeTab === tab.id ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700"
              }`}>{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {activeTab !== "archived" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="template-create-form">
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                <Plus size={12} /> Yeni Şablon Ekle
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-2">
                <input
                  data-testid="template-new-label"
                  value={newForm.label}
                  onChange={(e) => setNewForm({ ...newForm, label: e.target.value })}
                  maxLength={60}
                  placeholder="Başlık (max 60) · ör: Dosya adı yanlış"
                  className="w-full border border-emerald-200 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                <select
                  data-testid="template-new-category"
                  value={newForm.category}
                  onChange={(e) => setNewForm({ ...newForm, category: e.target.value })}
                  className="w-full border border-emerald-200 rounded-md px-2 py-1.5 text-sm bg-white"
                >
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <textarea
                data-testid="template-new-text"
                value={newForm.text}
                onChange={(e) => setNewForm({ ...newForm, text: e.target.value })}
                maxLength={500}
                rows={2}
                placeholder="Personel'e gösterilecek metin"
                className="w-full border border-emerald-200 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-emerald-700/70">{newForm.label.length}/60 · {newForm.text.length}/500</span>
                <button
                  data-testid="template-create-submit"
                  onClick={create}
                  disabled={busy || !newForm.label.trim() || !newForm.text.trim()}
                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-xs font-bold disabled:opacity-50"
                ><Plus size={12} /> Ekle</button>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            {filtered.length === 0 && (
              <div className="text-center text-sm text-slate-400 py-8 italic">
                {activeTab === "archived" ? "Arşivde şablon yok" : "Bu kategoride şablon yok"}
              </div>
            )}
            {filtered.map((t) => {
              const isEditing = editing?.id === t.id;
              const isArchived = !!t.archived;
              return (
                <div
                  key={t.id}
                  data-testid={`template-row-${t.id}`}
                  className={`border rounded-lg p-2.5 ${isEditing ? "bg-amber-50 border-amber-300" : isArchived ? "bg-slate-50 border-slate-200 opacity-80" : "bg-white border-slate-200"}`}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-2">
                        <input
                          data-testid={`template-edit-label-${t.id}`}
                          value={editing.label}
                          onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          maxLength={60}
                          className="w-full border border-amber-300 rounded-md px-2.5 py-1 text-sm bg-white"
                        />
                        <select
                          data-testid={`template-edit-category-${t.id}`}
                          value={editing.category || "diger"}
                          onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                          className="w-full border border-amber-300 rounded-md px-2 py-1 text-sm bg-white"
                        >
                          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      </div>
                      <textarea
                        data-testid={`template-edit-text-${t.id}`}
                        value={editing.text}
                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        maxLength={500}
                        rows={2}
                        className="w-full border border-amber-300 rounded-md px-2.5 py-1 text-sm bg-white"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setEditing(null)} className="text-xs font-semibold text-slate-600 hover:underline px-2">İptal</button>
                        <button
                          data-testid={`template-edit-save-${t.id}`}
                          onClick={saveEdit}
                          disabled={busy}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-2.5 py-1 rounded-md text-xs font-bold disabled:opacity-50"
                        >Kaydet</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-sm text-slate-800">{t.label}</span>
                          <span className={`inline-flex items-center text-[9px] font-bold border rounded-full px-1.5 ${CATEGORY_COLORS[t.category] || CATEGORY_COLORS.diger}`}>
                            {catLabel(t.category)}
                          </span>
                          {t.is_builtin && (
                            <span className="text-[9px] font-bold uppercase bg-slate-200 text-slate-700 rounded-full px-1.5">built-in</span>
                          )}
                          {isArchived && (
                            <span className="text-[9px] font-bold uppercase bg-slate-700 text-white rounded-full px-1.5">arşiv</span>
                          )}
                          {t.usage_count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-rose-100 text-rose-800 rounded-full px-1.5" title={`${t.usage_count} kez kullanıldı`}>
                              <BarChart3 size={9} /> {t.usage_count}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{t.text}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isArchived ? (
                          <button
                            data-testid={`template-unarchive-${t.id}`}
                            onClick={() => unarchive(t)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-md text-xs font-bold disabled:opacity-50"
                            title="Geri Yükle"
                          >Geri Yükle</button>
                        ) : (
                          <>
                            <button
                              data-testid={`template-edit-${t.id}`}
                              onClick={() => setEditing({ id: t.id, label: t.label, text: t.text, category: t.category || "diger" })}
                              className="w-7 h-7 rounded hover:bg-slate-100 text-slate-600 grid place-items-center"
                              title="Düzenle"
                            ><Edit size={13} /></button>
                            <button
                              data-testid={`template-archive-${t.id}`}
                              onClick={() => archive(t)}
                              disabled={busy}
                              className="w-7 h-7 rounded hover:bg-amber-50 text-amber-600 grid place-items-center disabled:opacity-50"
                              title="Arşivle (chip listesinden kaldır, geçmişi koru)"
                            ><Settings size={13} /></button>
                            {!t.is_builtin && (
                              <button
                                data-testid={`template-delete-${t.id}`}
                                onClick={() => removeHard(t)}
                                disabled={busy}
                                className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center disabled:opacity-50"
                                title="Kalıcı Sil"
                              ><Trash2 size={13} /></button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >Kapat</button>
        </div>
      </div>
      {importOpen && (
        <RejectTemplatesImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

// =========================
// RejectTemplatesImportModal — xlsx/csv bulk import
// =========================
function RejectTemplatesImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("bordromat_token") : "";
  const API_BASE = (window && window.location && window.location.origin) + "/api";

  const downloadTemplate = async (fmt) => {
    try {
      const res = await fetch(`${API_BASE}/prim/reject-templates/import-template?fmt=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Şablon indirilemedi");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sablon_import.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} şablonu indirildi`);
    } catch (e) {
      toast.error("İndirilemedi");
    }
  };

  const doImport = async () => {
    if (!file) { toast.error("Dosya seçin"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/prim/reject-templates/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`${r.data.created} yeni, ${r.data.updated} güncel`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarım başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/80 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg my-8" data-testid="templates-import-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Plus size={18} /> Şablonları Toplu İçe Aktar
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            <div className="font-bold mb-1">Kolonlar:</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li><b>Başlık</b> (zorunlu, max 60) · aynı başlık varsa güncellenir (built-in'ler korunur)</li>
              <li><b>Metin</b> (zorunlu, max 500)</li>
              <li><b>Kategori</b> (opsiyonel): eksik_bilgi / kalite / mukerrer / diger</li>
            </ul>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="templates-import-dl-xlsx"
              onClick={() => downloadTemplate("xlsx")}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-xs font-bold"
            >Şablon .xlsx</button>
            <button
              data-testid="templates-import-dl-csv"
              onClick={() => downloadTemplate("csv")}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-xs font-bold"
            >Şablon .csv</button>
            <input
              data-testid="templates-import-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="flex-1 min-w-[150px] text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:bg-white file:text-slate-700 file:font-semibold file:cursor-pointer hover:file:bg-slate-50"
            />
          </div>
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs" data-testid="templates-import-result">
              <div className="font-bold text-emerald-900">Sonuç ({result.format?.toUpperCase()}):</div>
              <ul className="mt-1 space-y-0.5 text-emerald-800">
                <li>• Yeni: <strong>{result.created}</strong></li>
                <li>• Güncellenen: <strong>{result.updated}</strong></li>
              </ul>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-amber-800">
                  <strong>Uyarılar ({result.errors.length}):</strong>
                  <ul className="list-disc list-inside mt-1">
                    {result.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50">
            {result ? "Kapat" : "İptal"}
          </button>
          {result ? (
            <button data-testid="templates-import-finish" onClick={onDone} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">Tamam</button>
          ) : (
            <button
              data-testid="templates-import-submit"
              onClick={doImport}
              disabled={!file || busy}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >{busy ? "İçe aktarılıyor..." : "İçe Aktar"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// =========================
// ApproveBlockDialog — mükerrer onaylı föy nedeniyle onay bloklandığında admin override
// =========================
function ApproveBlockDialog({ state, onCancel, onOverride, onOpenQuickView, onMergeComplete }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [merging, setMerging] = useState(null); // target_approved_id
  const CONFIRM_WORD = "ONAYLA";
  const canOverride = typed.trim().toUpperCase() === CONFIRM_WORD;

  const isSingle = state.mode === "single";
  const blockedList = isSingle
    ? [{ id: state.ids[0], blocking_prims: state.blocking_prims || [] }]
    : (state.blocked_prims || []);
  const totalBlockers = blockedList.reduce(
    (acc, b) => acc + (b.blocking_prims?.length || 0),
    0
  );
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso || ""; }
  };

  const doOverride = async () => {
    setConfirming(true);
    try { await onOverride(); }
    finally { setConfirming(false); }
  };

  const doMergeInto = async (pendingId, targetId, blockerLabel) => {
    const ok = window.confirm(
      `BİRLEŞTİR ONAYI\n\n` +
      `Bu pending prim '${blockerLabel}' adlı ONAYLI prime birleştirilecek:\n` +
      `• Tahsilat ve Vekalet değerleri TOPLANACAK (prim payı otomatik artacak)\n` +
      `• Giden kişiler BİRLEŞTİRİLECEK (max 4)\n` +
      `• Pending prim SİLİNECEK\n` +
      `• İşlem audit log'a yazılır\n\n` +
      `Devam etmek istiyor musunuz?`
    );
    if (!ok) return;
    setMerging(targetId);
    try {
      await onMergeComplete(pendingId, targetId);
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="approve-block-dialog">
      <div className="bg-white rounded-xl shadow-2xl border-2 border-red-300 w-full max-w-2xl my-8">
        <div className="p-5 border-b border-slate-200 bg-red-50 rounded-t-xl flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <div className="w-9 h-9 rounded-lg bg-red-600 text-white grid place-items-center shrink-0">
              <ShieldAlert size={18} />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-red-900">
                Onay BLOKE · Mükerrer Föy
              </h3>
              <p className="text-xs text-red-800 mt-0.5">
                {state.message || `${blockedList.length} prim için aynı föy zaten onaylanmış`} · onaylama <strong>engellendi</strong>
              </p>
            </div>
          </div>
          <button
            data-testid="approve-block-close"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg hover:bg-red-100 grid place-items-center shrink-0"
          ><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[55vh] overflow-y-auto">
          <div className="text-xs text-slate-700">
            Bu primlerin icra föyü <strong>önceden onaylanmış</strong>. Normal onaylamaya izin verilmedi.
            {isSingle ? (
              <> İki seçenek var: <strong>(1)</strong> onaylı prim ile <strong>birleştir</strong> (tahsilat/vekalet toplanır, giden kişiler birleşir) ya da <strong>(2) ADMIN OVERRIDE</strong> ile ayrı prim olarak zorla onayla.</>
            ) : (
              <> Admin isterseniz "<strong>ADMIN OVERRIDE</strong>" ile yine de onaylayabilirsiniz — bu işlem denetim kaydına özel olarak işlenir. (Birleştirme sadece tekli onaylamalarda kullanılabilir.)</>
            )}
          </div>
          {blockedList.map((b) => (
            <div
              key={b.id}
              data-testid={`approve-block-group-${b.id}`}
              className="border border-red-200 rounded-lg bg-red-50/40 p-2.5"
            >
              <div className="text-[11px] font-bold uppercase tracking-wider text-red-800 mb-1.5 flex items-center gap-1.5">
                <Lock size={10} />
                {isSingle
                  ? `Onaylamak istediğiniz prim`
                  : <>
                      Pending <span className="font-mono-tab">{b.id.slice(0, 8)}</span>
                      {b.alacakli_adi ? <> · {b.alacakli_adi}</> : null}
                      {b.icra_foy ? <> · Föy {b.icra_foy}</> : null}
                    </>}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Engelleyen onaylı prim(ler) · tıklayınca detay açılır
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(b.blocking_prims || []).map((m) => (
                  <div key={m.id} className="inline-flex items-stretch gap-0 rounded-md overflow-hidden border border-emerald-300 bg-white">
                    <button
                      type="button"
                      data-testid={`approve-block-blocker-${m.id}`}
                      onClick={() => onOpenQuickView(m.id)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-emerald-900 hover:bg-emerald-50 transition-colors"
                      title={`Detay için tıkla · ${m.dosya_adi || "—"} · ${m.alacakli_adi || "—"}`}
                    >
                      <CheckCircle2 size={10} className="text-emerald-600" />
                      <span className="font-mono-tab">{fmt(m.tarih)}</span>
                      <span className="opacity-50">·</span>
                      <span className="truncate max-w-[150px]">{m.dosya_adi || "—"}</span>
                      <span className="text-[9px] opacity-60">({m.onaylayan_email?.split("@")[0] || "?"})</span>
                    </button>
                    {isSingle && (
                      <button
                        type="button"
                        data-testid={`approve-block-merge-${m.id}`}
                        onClick={() => doMergeInto(b.id, m.id, m.dosya_adi || "—")}
                        disabled={merging === m.id || confirming}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold border-l border-emerald-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 disabled:opacity-50 disabled:cursor-wait transition-colors"
                        title="Bu onaylı prime birleştir — tahsilat/vekalet toplanır, giden kişiler birleşir"
                      >
                        <GitMerge size={10} />
                        {merging === m.id ? "..." : "Bu'ya Birleştir"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-[11px] text-amber-900">
            <div className="font-bold uppercase tracking-wider mb-0.5">Override onayı için</div>
            <div>
              Devam etmek için aşağıya <strong className="bg-amber-200 rounded px-1">{CONFIRM_WORD}</strong> yazın. Bu işlem geri alınamaz; audit log'a "<strong>OVERRIDE</strong>" olarak kaydedilir.
            </div>
            <input
              data-testid="approve-block-confirm-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={CONFIRM_WORD}
              className="mt-1.5 w-full border border-amber-300 rounded px-2 py-1 text-xs font-mono-tab focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
          </div>
          <div className="text-[11px] text-slate-500 italic">
            Toplam {totalBlockers} onaylı eşleşme bulundu. Onay kararını vermeden önce yukarıdaki chip'lere tıklayıp onaylı primlerin içeriğini doğrulayın.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            data-testid="approve-block-cancel"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >Vazgeç</button>
          <button
            data-testid="approve-block-override"
            onClick={doOverride}
            disabled={!canOverride || confirming}
            className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-800 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <ShieldAlert size={14} />
            {confirming ? "İşleniyor..." : "ADMIN OVERRIDE · Yine de Onayla"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================
// PrimQuickViewModal — mükerrer uyarı chip'lerinden tıklanan primin hızlı önizlemesi
// =========================
function PrimQuickViewModal({ primId, onClose }) {
  const [prim, setPrim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/prim/${primId}`)
      .then((r) => { if (active) setPrim(r.data); })
      .catch((e) => { if (active) setErr(e?.response?.data?.detail || "Prim yüklenemedi"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [primId]);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso || "—"; }
  };
  const fmtMoney = (v) => {
    const n = Number(v || 0);
    return n.toLocaleString("tr-TR", { minimumFractionDigits: 2 });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="prim-quickview-modal">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2 text-indigo-800">
              <Eye size={18} /> Prim Önizlemesi
              {prim?.onaylandi && <span className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"><CheckCircle2 size={10} /> Onaylı</span>}
              {prim && !prim.onaylandi && <span className="inline-flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"><Clock size={10} /> Bekleyen</span>}
              {prim?.onay_override && (
                <span
                  data-testid="prim-quickview-override-badge"
                  className="inline-flex items-center gap-1 bg-red-100 border border-red-400 text-red-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  title={`Admin OVERRIDE · ${prim.onay_override_blockers?.length || 0} mükerrer föy bypass edildi`}
                >
                  ⚠ OVERRIDE
                </span>
              )}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5 font-mono-tab">{primId}</p>
          </div>
          <button data-testid="prim-quickview-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading && <div className="text-center text-sm text-slate-500 py-6">Yükleniyor...</div>}
          {err && <div className="text-center text-sm text-red-700 py-6" data-testid="prim-quickview-err">{err}</div>}
          {prim && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <QvField label="Tarih">{prim.tarih ? prim.tarih.slice(0, 10).split("-").reverse().join(".") : "—"}</QvField>
                <QvField label="İcra Föy">{prim.icra_foy || "—"}</QvField>
                <QvField label="İcra Esas">{prim.icra_esas || "—"}</QvField>
                <QvField label="Oran">%{prim.oran || 0}</QvField>
                <QvField label="Dosya Adı" wide>{prim.dosya_adi || "—"}</QvField>
                <QvField label="Müvekkil" wide>{prim.alacakli_adi || "—"}</QvField>
                <QvField label="Tahsilat">{fmtMoney(prim.tahsilat_miktari)} ₺</QvField>
                <QvField label="Vekalet">{fmtMoney(prim.vekalet_ucreti)} ₺</QvField>
                <QvField label="Prim Payı">{fmtMoney(prim.prim_payi)} ₺</QvField>
                <QvField label="Varlık Şirketi">{prim.varlik_sirketi ? "Evet" : "Hayır"}</QvField>
              </div>
              {prim.giden_isimleri?.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Giden Kişiler</div>
                  <div className="flex flex-wrap gap-1">
                    {prim.giden_isimleri.map((n, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2 py-0.5 text-xs font-semibold text-slate-700">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {prim.onaylandi && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-xs">
                  <div className="font-bold text-emerald-900 flex items-center gap-1.5"><CheckCircle2 size={12} /> Onay Bilgileri</div>
                  <div className="mt-1 text-emerald-800">
                    <strong>Onaylayan:</strong> {prim.onaylayan_email || "—"}
                  </div>
                  <div className="text-emerald-800">
                    <strong>Onay Tarihi:</strong> {fmtDate(prim.onay_tarihi)}
                  </div>
                </div>
              )}
              {prim.created_by_email && (
                <div className="text-[11px] text-slate-500 italic">
                  Oluşturan: {prim.created_by_email} ({prim.created_by_role || "—"}) · {fmtDate(prim.created_at)}
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button data-testid="prim-quickview-close-btn" onClick={onClose} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function QvField({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate">{children}</div>
    </div>
  );
}

// =========================
// MuhasebeciNoteCell — muhasebeci (ya da admin) pending prim'e not düşer / düzenler / siler
// Admin ve muhasebeci okur ve yazar; personel bu tab'ı zaten görmez.
// =========================
function MuhasebeciNoteCell({ row, open, onOpen, onClose, onSaved }) {
  const existing = row?.muhasebeci_note || null;
  const [draft, setDraft] = useState(existing?.text || "");
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    setDraft(existing?.text || "");
  }, [existing?.text, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onClose]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/prim/${row.id}/muhasebeci-note`, { note: draft });
      toast.success(draft.trim() ? "Not kaydedildi" : "Not silindi");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };

  if (!open) {
    const hasNote = !!existing;
    return (
      <button
        type="button"
        data-testid={`muhasebeci-note-btn-${row.id}`}
        onClick={onOpen}
        title={hasNote ? `${existing.text}\n— ${existing.by_email} (${fmt(existing.at)})${row.muhasebeci_note_seen_by_admin ? "\n✓ Admin tarafından okundu" : "\n⏳ Admin henüz okumadı"}` : "Muhasebeci notu ekle · admin'e bilgi düş"}
        className={`relative inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
          hasNote
            ? "bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300"
            : "text-slate-400 hover:text-slate-700 hover:bg-slate-100 border border-dashed border-slate-200"
        }`}
      >
        {hasNote ? <MessageSquare size={13} /> : <Plus size={13} />}
        {hasNote && (
          <span
            data-testid={`muhasebeci-note-status-${row.id}`}
            className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border border-white ${
              row.muhasebeci_note_seen_by_admin ? "bg-emerald-500" : "bg-rose-500"
            }`}
            title={row.muhasebeci_note_seen_by_admin ? "Admin okudu" : "Admin henüz okumadı"}
          />
        )}
      </button>
    );
  }

  return (
    <div
      ref={ref}
      data-testid={`muhasebeci-note-popover-${row.id}`}
      className="absolute right-24 z-30 bg-white border border-amber-300 rounded-lg shadow-xl p-3 w-72 text-left"
      style={{ marginTop: -8 }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
          <MessageSquare size={11} /> Muhasebeci Notu
        </div>
        <button
          type="button"
          data-testid={`muhasebeci-note-close-${row.id}`}
          onClick={onClose}
          className="w-5 h-5 rounded hover:bg-slate-100 grid place-items-center"
        ><X size={11} /></button>
      </div>
      {existing && (
        <div className="text-[10px] text-slate-500 mb-1.5 italic">
          Son güncelleme: {existing.by_email} · {fmt(existing.at)}
        </div>
      )}
      <textarea
        data-testid={`muhasebeci-note-textarea-${row.id}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="Admin'e not: örn. tahsilat tutarı belgeyle uyuşmuyor, kontrol edin"
        className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[9px] text-slate-400">{draft.length}/500</span>
        <div className="flex gap-1">
          {existing && (
            <button
              data-testid={`muhasebeci-note-clear-${row.id}`}
              onClick={() => { setDraft(""); setTimeout(save, 0); }}
              disabled={busy}
              className="px-2 py-0.5 rounded text-[11px] font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >Sil</button>
          )}
          <button
            data-testid={`muhasebeci-note-save-${row.id}`}
            onClick={save}
            disabled={busy || draft === (existing?.text || "")}
            className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
          >{busy ? "..." : "Kaydet"}</button>
        </div>
      </div>
    </div>
  );
}
